/**
 * Filters product gallery to the selected variant / shade media only.
 * Modes:
 * - even-split: product.media ordered in equal blocks per shade (default for Bindt)
 * - group: featured_media markers start each shade's block
 * Never reveals the full gallery when hide_variants is on.
 */
(function () {
  const HIDE_VIEWER = 'product__media-item--hide-variant';
  const HIDE_THUMB = 'thumbnail-list__item--hide-variant';

  function getGalleries(sectionId) {
    if (sectionId) {
      const exact =
        document.getElementById(`MediaGallery-${sectionId}`) ||
        document.querySelector(`media-gallery[data-section="${sectionId}"]`);
      if (exact) return [exact];
    }
    return Array.from(document.querySelectorAll('media-gallery')).filter(
      (g) => g.dataset.hideVariants !== 'false' && g.querySelector('[data-variant-group], [data-media-index]')
    );
  }

  function uniqueGalleries(list) {
    return Array.from(new Set(list));
  }

  function setItemVisibility(item, visible, hideClass) {
    item.classList.toggle(hideClass, !visible);
    if (visible) {
      item.removeAttribute('hidden');
      item.style.removeProperty('display');
    } else {
      item.setAttribute('hidden', '');
      item.style.setProperty('display', 'none', 'important');
    }
  }

  function countVisibleViewer(gallery) {
    return gallery.querySelectorAll(
      `.product__media-list [data-media-index]:not(.${HIDE_VIEWER}), .product__media-list [data-variant-group]:not(.${HIDE_VIEWER})`
    ).length;
  }

  function updateCounter(gallery, currentPage) {
    const viewer = getViewerSlider(gallery);
    const total = viewer?.querySelector('.slider-counter--total') || gallery.querySelector('.slider-counter--total');
    const current =
      viewer?.querySelector('.slider-counter--current') || gallery.querySelector('.slider-counter--current');
    const visible = Math.max(countVisibleViewer(gallery), 1);
    if (total) total.textContent = String(visible);
    if (current) current.textContent = String(currentPage || 1);
  }

  function getViewerSlider(gallery) {
    return (
      gallery.querySelector('slider-component.thumbnails-left__viewer') ||
      gallery.querySelector('[id^="GalleryViewer-"]') ||
      gallery.querySelector('slider-component.slider-mobile-gutter:not(.thumbnail-slider)')
    );
  }

  function resetMobileViewer(gallery, mediaId) {
    const viewer = getViewerSlider(gallery);
    const list = viewer?.querySelector('.product__media-list, [id^="Slider-Gallery"]');
    if (!viewer || !list) return;

    // Scroll to the active/visible slide (mobile slider uses horizontal scroll)
    const active =
      (mediaId && list.querySelector(`[data-media-id="${mediaId}"]:not(.${HIDE_VIEWER})`)) ||
      list.querySelector(`.product__media-item:not(.${HIDE_VIEWER})`);

    if (active) {
      // Put active slide first in the list so mobile page 1 is never a hidden slide
      if (list.firstElementChild !== active) {
        list.prepend(active);
      }
      list.scrollLeft = 0;
      try {
        list.scrollTo({ left: 0, behavior: 'instant' });
      } catch (e) {
        list.scrollLeft = 0;
      }
    } else {
      list.scrollLeft = 0;
    }

    if (typeof viewer.resetPages === 'function') {
      try {
        viewer.resetPages();
      } catch (e) {
        /* no-op */
      }
    }

    // Dawn caches slide metrics — force refresh after display:none changes
    if (typeof viewer.initPages === 'function') {
      try {
        viewer.initPages();
      } catch (e) {
        /* no-op */
      }
    }

    updateCounter(gallery, 1);
  }

  function ensureSingleActive(gallery, mediaId) {
    gallery.querySelectorAll('.product__media-list [data-media-id]').forEach((item) => {
      item.classList.toggle(
        'is-active',
        Boolean(mediaId) && item.dataset.mediaId === mediaId && !item.classList.contains(HIDE_VIEWER)
      );
    });

    gallery.querySelectorAll('.thumbnail-list [data-target]').forEach((item) => {
      const button = item.querySelector('button');
      if (!button) return;
      if (mediaId && item.dataset.target === mediaId && !item.classList.contains(HIDE_THUMB)) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  function syncLeftThumbnailRail(gallery) {
    const rail = gallery.querySelector('.thumbnail-slider--left');
    const viewer = gallery.querySelector('.thumbnails-left__viewer');
    const list = rail?.querySelector('.thumbnail-list');
    if (!rail || !viewer || !list) return;

    const viewerHeight = viewer.offsetHeight;
    if (viewerHeight > 0) rail.style.height = `${viewerHeight}px`;

    list.style.transform = 'none';
    list.scrollTop = 0;
    list.scrollLeft = 0;
  }

  function itemBelongs(item, gallery, variantId, optionValue, featuredMediaId, sectionId) {
    const mode = gallery.dataset.filterMode || 'even-split';
    const shadeCount = Math.max(parseInt(gallery.dataset.shadeCount || '1', 10), 1);
    const shadeIndex = Math.max(parseInt(gallery.dataset.selectedShadeIndex || '0', 10), 0);
    const mediaIndex = parseInt(item.dataset.mediaIndex || '-1', 10);
    const mediaTotal = gallery.querySelectorAll('.product__media-list [data-media-index]').length;
    const perShade = Math.max(
      parseInt(gallery.dataset.perShade || '0', 10) || Math.floor(mediaTotal / shadeCount) || 1,
      1
    );

    // Featured for selected variant always belongs
    if (featuredMediaId && sectionId && item.dataset.mediaId === `${sectionId}-${featuredMediaId}`) {
      return true;
    }

    // Alt text contains shade name
    const alt = String(item.dataset.mediaAlt || item.querySelector('img')?.alt || '').toLowerCase();
    const opt = String(optionValue || gallery.dataset.selectedOptionValue || '').toLowerCase().trim();
    if (opt && alt.includes(opt)) return true;

    if (mode === 'group') {
      return String(item.dataset.variantGroup) === String(variantId);
    }

    // even-split by media order
    if (mediaIndex < 0) return false;
    let start = shadeIndex * perShade;
    let end = start + perShade - 1;
    if (shadeIndex === shadeCount - 1) end = mediaTotal - 1;
    return mediaIndex >= start && mediaIndex <= end;
  }

  function applyVisibility(gallery, variantId, optionValue, featuredMediaId) {
    const sectionId =
      gallery.id?.replace(/^MediaGallery-/, '') || gallery.closest('[data-section]')?.dataset?.section;

    gallery.querySelectorAll('.product__media-list [data-media-index], .product__media-list [data-variant-group]').forEach((item) => {
      const visible = itemBelongs(item, gallery, variantId, optionValue, featuredMediaId, sectionId);
      setItemVisibility(item, visible, HIDE_VIEWER);
      item.classList.remove('is-active');
    });

    gallery.querySelectorAll('.thumbnail-list [data-media-index], .thumbnail-list [data-variant-group]').forEach((item) => {
      const visible = itemBelongs(item, gallery, variantId, optionValue, featuredMediaId, sectionId);
      setItemVisibility(item, visible, HIDE_THUMB);
      item.querySelector('button')?.removeAttribute('aria-current');
    });

    // Last resort: show featured only (never the full gallery)
    if (countVisibleViewer(gallery) === 0 && featuredMediaId && sectionId) {
      const featured = gallery.querySelector(
        `.product__media-list [data-media-id="${sectionId}-${featuredMediaId}"]`
      );
      if (featured) {
        setItemVisibility(featured, true, HIDE_VIEWER);
        const thumb = gallery.querySelector(
          `.thumbnail-list [data-target="${sectionId}-${featuredMediaId}"]`
        );
        if (thumb) setItemVisibility(thumb, true, HIDE_THUMB);
      }
    }

    // Still empty → show first media item only
    if (countVisibleViewer(gallery) === 0) {
      const first = gallery.querySelector('.product__media-list [data-media-id]');
      if (first) {
        setItemVisibility(first, true, HIDE_VIEWER);
        const thumb = gallery.querySelector(`.thumbnail-list [data-target="${first.dataset.mediaId}"]`);
        if (thumb) setItemVisibility(thumb, true, HIDE_THUMB);
      }
    }
  }

  function filterGallery(gallery, variantId, featuredMediaId, optionValue) {
    if (!gallery || gallery.dataset.hideVariants === 'false') return;

    const resolvedVariantId = String(variantId || gallery.dataset.selectedVariantId || '');
    if (!resolvedVariantId) return;

    const resolvedFeaturedId = featuredMediaId || gallery.dataset.featuredMediaId || null;
    const resolvedOption =
      optionValue || gallery.dataset.selectedOptionValue || '';

    gallery.dataset.selectedVariantId = resolvedVariantId;
    if (resolvedFeaturedId) gallery.dataset.featuredMediaId = String(resolvedFeaturedId);
    if (resolvedOption) gallery.dataset.selectedOptionValue = resolvedOption;

    applyVisibility(gallery, resolvedVariantId, resolvedOption, resolvedFeaturedId);

    const sectionId =
      gallery.id?.replace(/^MediaGallery-/, '') || gallery.closest('[data-section]')?.dataset?.section;
    const preferredId =
      resolvedFeaturedId && sectionId ? `${sectionId}-${resolvedFeaturedId}` : null;
    const preferredVisible =
      preferredId &&
      gallery.querySelector(
        `.product__media-list [data-media-id="${preferredId}"]:not(.${HIDE_VIEWER})`
      );
    const firstVisible = gallery.querySelector(
      `.product__media-list [data-media-id]:not(.${HIDE_VIEWER})`
    )?.dataset?.mediaId;
    const mediaId = preferredVisible ? preferredId : firstVisible;

    // Activate + move featured/first visible to the start so mobile isn't stuck on a hidden slide
    if (mediaId && typeof gallery.setActiveMedia === 'function') {
      try {
        gallery.setActiveMedia(mediaId, true);
      } catch (e) {
        /* no-op */
      }
    }

    ensureSingleActive(gallery, mediaId);
    resetMobileViewer(gallery, mediaId);
    updateCounter(gallery, 1);

    requestAnimationFrame(() => {
      resetMobileViewer(gallery, mediaId);
      syncLeftThumbnailRail(gallery);
    });
  }

  function filterAll(variantId, featuredMediaId, sectionId, optionValue) {
    uniqueGalleries(getGalleries(sectionId)).forEach((gallery) => {
      filterGallery(gallery, variantId, featuredMediaId, optionValue);
    });
  }

  window.BindtVariantMedia = {
    filter: filterAll,
    filterGallery,
  };

  function onVariantChange(event) {
    const variant = event?.data?.variant;
    if (!variant?.id) return;

    const optionValue =
      variant.option1 || variant.options?.[0] || event?.data?.optionValue || null;

    // Run after Dawn product-info finishes morphing / scrolling the gallery
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        uniqueGalleries(getGalleries(event?.data?.sectionId)).forEach((gallery) => {
          if (optionValue) {
            gallery.dataset.selectedOptionValue = String(optionValue);
            const sectionId = gallery.id?.replace(/^MediaGallery-/, '');
            const checked = document.querySelectorAll(
              `#variant-selects-${sectionId} input[type="radio"]:checked`
            );
            checked.forEach((shadeInput) => {
              const group = shadeInput.closest('fieldset, .product-form__input');
              const radios = group ? Array.from(group.querySelectorAll('input[type="radio"]')) : [];
              const idx = radios.findIndex((r) => r === shadeInput || r.value === shadeInput.value);
              if (idx >= 0) gallery.dataset.selectedShadeIndex = String(idx);
            });
          }
          filterGallery(gallery, variant.id, variant.featured_media?.id, optionValue);
        });
      });
    });
  }

  function boot() {
    uniqueGalleries(getGalleries()).forEach((gallery) => {
      filterGallery(
        gallery,
        gallery.dataset.selectedVariantId,
        gallery.dataset.featuredMediaId || null,
        gallery.dataset.selectedOptionValue || null
      );
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  } else {
    document.addEventListener('variant:change', (e) => onVariantChange({ data: e.detail }));
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLSelectElement)) return;

    // Shade swatch change → update shade index before id changes
    if (input.type === 'radio' && input.closest('variant-selects, #variant-selects-' + (input.closest('[data-section]')?.dataset?.section || ''))) {
      const group = input.closest('fieldset, .product-form__input');
      const radios = group ? Array.from(group.querySelectorAll('input[type="radio"]')) : [];
      const idx = radios.indexOf(input);
      const sectionId = input.closest('[data-section]')?.dataset?.section;
      uniqueGalleries(getGalleries(sectionId)).forEach((gallery) => {
        if (idx >= 0) gallery.dataset.selectedShadeIndex = String(idx);
        gallery.dataset.selectedOptionValue = String(input.value || '');
      });
    }

    if (input.name !== 'id' || !input.closest('product-info, .product, form[action*="/cart/add"]')) return;
    if (!/^\d+$/.test(input.value)) return;

    const sectionId = input.closest('[data-section]')?.dataset?.section;
    requestAnimationFrame(() => {
      uniqueGalleries(getGalleries(sectionId)).forEach((gallery) => {
        filterGallery(
          gallery,
          input.value,
          gallery.dataset.featuredMediaId || null,
          gallery.dataset.selectedOptionValue || null
        );
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
})();